import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { translations, type Language } from './translations'
import { extendedTranslations } from './extendedTranslations'
import { platformTranslations } from './platformTranslations'

const STORAGE_KEY = 'magacin-language'

interface LanguageContextValue {
  language: Language
  setLanguage: (language: Language) => void
  t: (source: string) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

function savedLanguage(): Language {
  const value = window.localStorage.getItem(STORAGE_KEY)
  return value === 'sq' || value === 'mk' || value === 'en' ? value : 'en'
}

export function LanguageProvider({ children }: PropsWithChildren) {
  const [language, setLanguage] = useState<Language>(savedLanguage)

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language)
    document.documentElement.lang = language
  }, [language])

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    setLanguage,
    t: (source) => language === 'en' ? source : platformTranslations[language][source] ?? extendedTranslations[language][source] ?? translations[language][source] ?? source,
  }), [language])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used within LanguageProvider')
  return context
}
